/**
 * Case Study Template Component
 * 
 * This file contains a reusable template for creating case study pages.
 * 
 * To create a new case study:
 * 1. Define a CaseStudyConfig object with your project details
 * 2. Create a new component that uses <CaseStudy config={yourConfig} />
 * 3. Add the route to index.tsx
 * 
 * Example:
 * 
 * const myProjectConfig: CaseStudyConfig = {
 *   storageKey: 'my_project_case_study',
 *   title: 'My Project Title',
 *   description: 'Project description...',
 *   modules: [
 *     { src: '/projects/MyProject/Image1.jpg', alt: 'Hero Image', type: 'single' },
 *     { src: '/projects/MyProject/Image2.jpg', alt: 'Detail 1', type: 'single' },
 *     { src: '/projects/MyProject/Image3.jpg', alt: 'Detail 2', type: 'twoColumn', pairIndex: 0 },
 *     { src: '/projects/MyProject/Image4.jpg', alt: 'Detail 3', type: 'twoColumn', pairIndex: 0 },
 *   ],
 *   creditsTitle: 'Credits',
 *   creditsCopy: 'Client Name\nDesign Team\nDevelopment Team\nYear'
 * };
 * 
 * export const MyProject = (): JSX.Element => {
 *   return <CaseStudy config={myProjectConfig} />;
 * };
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../../components/Header';
import { SEO } from '../../components/SEO';
import { JsonLd } from '../../components/JsonLd';
import { ClickAnimation } from '../../components/ClickAnimation';
import { motion } from 'framer-motion';
import { ScrollIndicator } from '../../components/ScrollIndicator';
import { TwoColumnImages } from '../../components/TwoColumnImages';
import { TwoColumnExperienceLinks } from '../../components/TwoColumnExperienceLinks';
import { VideoPlayer } from '../../components/VideoPlayer';
import { ImageCarousel } from '../../components/ImageCarousel';
import { BodyText } from '../../components/BodyText';
import { NextProject } from '../../components/NextProject';
import { getVideoUrl } from '../../lib/videoUrls';
import { getImageUrl } from '../../lib/imageUrls';
import { createFlexibleCaseStudyConfig, ImageInfo, VideoInfo } from '../../utils/caseStudyGenerator';
import { SITE_URL, getCreativeWorkSchema } from '../../lib/seo';

// --- BEGIN: Case Study Template Configuration ---
// Default configuration for Volvo Cars Configurator case study
const DEFAULT_TITLE = 'Volvo Cars Configurator';
const DEFAULT_DESCRIPTION = 'An innovative digital platform that revolutionizes how customers configure and personalize their Volvo vehicles, offering an immersive and intuitive experience.';
const DEFAULT_MODULES: CaseStudyConfig['modules'] = [
  { src: getImageUrl('/projects/VolvoConfig/Image1.jpg'), alt: 'Volvo Cars Configurator Hero', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image2.jpg'), alt: 'Volvo Configurator Overview', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image3.jpg'), alt: 'Volvo Configurator Detail 1', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image4.jpg'), alt: 'Volvo Configurator Detail 2', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image5.jpg'), alt: 'Volvo Configurator Detail 3', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image6.jpg'), alt: 'Volvo Configurator Detail 4', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image2.jpg'), alt: 'Volvo Configurator Detail 5', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image3.jpg'), alt: 'Volvo Configurator Detail 6', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image4.jpg'), alt: 'Volvo Configurator Detail 7', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image5.jpg'), alt: 'Volvo Configurator Detail 8', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image2.jpg'), alt: 'Volvo Configurator Overview', type: 'twoColumn', pairIndex: 0 },
  { src: getImageUrl('/projects/VolvoConfig/Image3.jpg'), alt: 'Volvo Configurator Detail 1', type: 'twoColumn', pairIndex: 0 },
  { src: getImageUrl('/projects/VolvoConfig/Image4.jpg'), alt: 'Volvo Configurator Detail 7', type: 'single' },
  { src: getImageUrl('/projects/VolvoConfig/Image2.jpg'), alt: 'Volvo Configurator Overview', type: 'twoColumn', pairIndex: 1 },
  { src: getImageUrl('/projects/VolvoConfig/Image3.jpg'), alt: 'Volvo Configurator Detail 1', type: 'twoColumn', pairIndex: 1 },
];

function getLocal(key: string, fallback: any) {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : fallback;
  } catch {
    return fallback;
  }
}

// --- END: Case Study Template Configuration ---

// Project list matching the homepage order
const PROJECTS = [
  { name: "Mack Trucks", image: getImageUrl("/3d-homepage/img4.png"), link: "/mack-trucks" },
  { name: "Volvo Trucks North America", image: getImageUrl("/projects/3D-viewer/img25.png"), link: "/volvo-trucks-na" },
  { name: "Zeekr", image: getImageUrl("/3d-homepage/img8.png"), link: "/zeekr" },
  { name: "Volvo Cars", image: getImageUrl("/3d-homepage/img1.png"), link: "/volvo-configurator", locked: true },
  { name: "Polestar Engineered", image: "/projects/PolestarEngineered/PolestarEngineered-Thumb.png", link: "/polestar-engineered" },
  { name: "Volvo Trucks AR", image: getImageUrl("/projects/3D-viewer/img16.png"), link: "/volvo-trucks-ar" },
  { name: "Formula 1", image: getImageUrl("/3d-homepage/img3.png"), link: "/formula1" },
  { name: "Drita Ime app", image: getImageUrl("/_images/DritaIme/dritaime-thumb-promo.png"), link: "/drita-ime" },
  { name: "AI Playground", image: getImageUrl("/_images/3d-homepage/image-thumb-ai.png"), link: "/ai-playground" },
  { name: "Word Play", image: getImageUrl("/_images/WordPlay/wordplay-thumb.jpg"), link: "/word-play" },
  { name: "Nivea", image: getImageUrl("/3d-homepage/img7.png"), link: "/nivea" },
  { name: "Nike Tech Pack", image: getImageUrl("/projects/3D-viewer/img17.png"), link: "/nike" },
  { name: "Nissan IDx", image: getImageUrl("/projects/3D-viewer/img24.png"), link: "/nissan-idx" },
  { name: "Volvo Trucks Aero", image: getImageUrl("/projects/3D-viewer/img18.png"), link: "/volvo-trucks-aero" },
  { name: "Dieter Rams", image: getImageUrl("/projects/3D-viewer/img21.png"), link: "/dieter-rams" },
  { name: "H&M", image: getImageUrl("/3d-homepage/img11.png"), link: "#", locked: true },
  { name: "Mack Trucks AR", image: getImageUrl("/projects/3D-viewer/img22.png"), link: "#", locked: true },
  { name: "Volvo Rider", image: getImageUrl("/3d-homepage/img2.png"), link: "#", locked: true },
  { name: "Lamborghini", image: getImageUrl("/3d-homepage/img12.png"), link: "#", locked: true },
  { name: "Other", image: getImageUrl("/projects/3D-viewer/img9.png"), link: "#", locked: true }
];

// Generic Case Study Component Interface
interface CaseStudyConfig {
  storageKey: string;
  title: string;
  description: string;
  /** When true, prevents indexing (for locked/sensitive projects) */
  noindex?: boolean;
  modules: Array<{
    src: string;
    alt: string;
    type: 'single' | 'twoColumn' | 'video' | 'carousel' | 'text';
    pairIndex?: number;
    videoId?: string; // For YouTube videos
    videoType?: 'mp4' | 'youtube' | 'vimeo'; // Video type
    vimeoEmbed?: string; // For Vimeo embed HTML
    images?: Array<{ src: string; alt: string; }>; // For carousel modules
    text?: string; // For text modules
    loop?: boolean; // For looping videos
    muted?: boolean; // For muted videos
    preserveAspectRatio?: boolean; // Preserve original video aspect ratio
  }>;
  creditsTitle: string;
  creditsCopy: string;
  hideCredits?: boolean;
  experienceLink?: {
    label: string;
    href: string;
    afterParagraph: number;
  };
  experienceLinks?: Array<{
    label: string;
    href: string;
    afterParagraph: number;
    image?: string;
    imageAlt?: string;
  }>;
  contactSocials?: Array<{
    label: string;
    href: string;
  }>;
}

// Generic Case Study Component
export const CaseStudy = ({ config }: { config: CaseStudyConfig }): JSX.Element => {
  const location = useLocation();
  const caseStudyImages = getLocal(config.storageKey + '_modules', config.modules);
  const caseStudyTitle = getLocal(config.storageKey + '_title', config.title);
  const caseStudyDesc = getLocal(config.storageKey + '_desc', config.description);
  const caseStudyCreditsTitle = getLocal(config.storageKey + '_credits_title', config.creditsTitle);
  const caseStudyCreditsCopy = getLocal(config.storageKey + '_credits_copy', config.creditsCopy);
  const [isHeaderDark, setIsHeaderDark] = useState(false);
  const noindex = config.noindex ?? false;
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const heroImage = caseStudyImages.find((m) => (m.type === 'single' || m.type === 'twoColumn') && m.src)?.src ?? '';
  const [isNextProjectVisible, setIsNextProjectVisible] = useState(false);
  const [clickAnimation, setClickAnimation] = useState<{ visible: boolean; position: { x: number; y: number } }>({
    visible: false,
    position: { x: 0, y: 0 }
  });
  const contentRef = useRef<HTMLDivElement>(null);

  // Create refs for each image module
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  if (imageRefs.current.length !== caseStudyImages.length) {
    imageRefs.current = Array(caseStudyImages.length).fill(null);
  }

  // Create a ref object for each image module for useInView
  const refObjects = useRef(caseStudyImages.map(() => React.createRef<HTMLDivElement>()));
  if (refObjects.current.length !== caseStudyImages.length) {
    refObjects.current = caseStudyImages.map(() => React.createRef<HTMLDivElement>());
  }

  // Track which image is most in view
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      let maxRatio = 0;
      let maxIdx = 0;
      const viewportHeight = window.innerHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Detect if we're on mobile
      const isMobile = window.innerWidth <= 768;
      
      refObjects.current.forEach((ref: React.RefObject<HTMLDivElement>, idx: number) => {
        const el = ref.current;
        if (!el) return;
        
        const rect = el.getBoundingClientRect();
        const elementTop = rect.top + scrollTop;
        const elementBottom = elementTop + rect.height;
        
        // Calculate intersection with viewport center
        const viewportCenter = scrollTop + viewportHeight / 2;
        const elementCenter = elementTop + rect.height / 2;
        
        // Calculate distance from viewport center
        const distanceFromCenter = Math.abs(elementCenter - viewportCenter);
        
        // Calculate how much of the element is visible
        const visibleTop = Math.max(elementTop, scrollTop);
        const visibleBottom = Math.min(elementBottom, scrollTop + viewportHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const visibilityRatio = visibleHeight / rect.height;
        
        // Adjust scoring based on device type
        let centerWeight, score;
        if (isMobile) {
          // On mobile, prioritize elements closer to the top of viewport
          const topDistance = Math.abs(elementTop - scrollTop);
          centerWeight = Math.max(0, 1 - topDistance / viewportHeight);
          score = visibilityRatio * 0.5 + centerWeight * 0.5;
        } else {
          // On desktop, use center-based scoring
          centerWeight = Math.max(0, 1 - distanceFromCenter / viewportHeight);
          score = visibilityRatio * 0.7 + centerWeight * 0.3;
        }
        
        if (score > maxRatio) {
          maxRatio = score;
          maxIdx = idx;
        }
      });
      setActiveIndex(maxIdx);
    };
    
    // Use requestAnimationFrame for smoother updates
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, []);

  // Update header color based on scroll position
  useEffect(() => {
    const deviceSection = document.querySelector('#device-section');
    if (deviceSection) {
      const deviceSectionTop = deviceSection.getBoundingClientRect().top;
      setIsHeaderDark(deviceSectionTop > 0);
    }
  }, []);

  // Handle click animation
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Only trigger animation for left mouse button clicks
      if (event.button !== 0) return;
      
      // Prevent conflicts with existing click handlers by using a small delay
      requestAnimationFrame(() => {
        setClickAnimation({
          visible: true,
          position: { x: event.clientX, y: event.clientY }
        });

        // Hide animation after it completes (1.08 seconds based on 27 frames at 25fps)
        setTimeout(() => {
          setClickAnimation(prev => ({ ...prev, visible: false }));
        }, 1080);
      });
    };

    // Use capture phase to ensure we catch clicks before other handlers
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  // Helper: get indices to highlight based on activeIndex and two-column pairs
  // Filter out text modules for scroll indicator (only show images and videos)
  const scrollIndicatorModules = caseStudyImages.filter((module: typeof caseStudyImages[0]) => module.type !== 'text');
  
  // Create a mapping from original indices to filtered indices
  const indexMap = new Map<number, number>();
  let filteredIdx = 0;
  caseStudyImages.forEach((module: typeof caseStudyImages[0], originalIdx: number) => {
    if (module.type !== 'text') {
      indexMap.set(originalIdx, filteredIdx);
      filteredIdx++;
    }
  });

  const getHighlightIndices = () => {
    // Find all two-column pairs
    const pairs: [number, number][] = [];
    let i = 0;
    while (i < caseStudyImages.length) {
      if (
        caseStudyImages[i].type === 'twoColumn' &&
        i + 1 < caseStudyImages.length &&
        caseStudyImages[i + 1].type === 'twoColumn' &&
        caseStudyImages[i].pairIndex === caseStudyImages[i + 1].pairIndex
      ) {
        pairs.push([i, i + 1]);
        i += 2;
        continue;
      }
      i += 1;
    }
    // Check if activeIndex is in a two-column pair
    for (const [a, b] of pairs) {
      if (activeIndex === a || activeIndex === b) {
        // Map original indices to filtered indices for scroll indicator
        const mappedA = indexMap.get(a);
        const mappedB = indexMap.get(b);
        if (mappedA !== undefined && mappedB !== undefined) {
          return [mappedA, mappedB];
        }
      }
    }
    // Map the active index to filtered index
    const mappedIndex = indexMap.get(activeIndex);
    return mappedIndex !== undefined ? [mappedIndex] : [];
  };

  // Helper to render image modules from the flat array, skipping the first (hero) image
  const renderImageModules = () => {
    const modules = [];
    let i = 1; // start from 1 to skip the hero image
    while (i < caseStudyImages.length) {
      const img = caseStudyImages[i] as CaseStudyConfig['modules'][0];
      if (img.type === 'twoColumn') {
        // Render TwoColumnImages for each pair
        if (i + 1 < caseStudyImages.length && caseStudyImages[i + 1].type === 'twoColumn' && caseStudyImages[i].pairIndex === caseStudyImages[i + 1].pairIndex) {
          modules.push(
            <section className="w-full py-10" key={`twocolumn-${i}`} ref={refObjects.current[i]}> 
              <div className="w-full max-w-[1280px] mx-auto px-4 space-y-10">
                <TwoColumnImages
                  image1={getImageUrl(caseStudyImages[i].src)}
                  image2={getImageUrl(caseStudyImages[i + 1].src)}
                  alt1={caseStudyImages[i].alt}
                  alt2={caseStudyImages[i + 1].alt}
                />
              </div>
            </section>
          );
          i += 2;
          continue;
        }
      }

      // Handle video pairs
      if (img.type === 'video' && img.pairIndex !== undefined) {
        if (i + 1 < caseStudyImages.length && caseStudyImages[i + 1].type === 'video' && caseStudyImages[i].pairIndex === caseStudyImages[i + 1].pairIndex) {
          modules.push(
            <section className="w-full py-10" key={`videopair-${i}`} ref={refObjects.current[i]}> 
              <div className="w-full max-w-[1280px] mx-auto px-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <VideoPlayer
                    src={caseStudyImages[i].src}
                    title={caseStudyImages[i].alt}
                    className="w-full"
                    loop={caseStudyImages[i].loop || false}
                    muted={caseStudyImages[i].muted || false}
                    rounded={true}
                    preserveAspectRatio={caseStudyImages[i].preserveAspectRatio || false}
                  />
                  <VideoPlayer
                    src={caseStudyImages[i + 1].src}
                    title={caseStudyImages[i + 1].alt}
                    className="w-full"
                    loop={caseStudyImages[i + 1].loop || false}
                    muted={caseStudyImages[i + 1].muted || false}
                    rounded={true}
                    preserveAspectRatio={caseStudyImages[i + 1].preserveAspectRatio || false}
                  />
                </div>
              </div>
            </section>
          );
          i += 2;
          continue;
        }
      }
      
      // Render video module
      if (img.type === 'video') {
        modules.push(
          <section className="w-full py-10" key={`video-${i}`} ref={refObjects.current[i]}> 
            <div className="w-full max-w-[1280px] mx-auto px-4 space-y-10">
              {img.videoType === 'mp4' ? (
                <VideoPlayer
                  src={img.src}
                  title={img.alt}
                  className="w-full"
                  loop={img.loop || false}
                  muted={img.muted || false}
                  preserveAspectRatio={img.preserveAspectRatio || false}
                />
              ) : img.videoType === 'youtube' ? (
                <div className="w-full aspect-video">
                  <iframe
                    src={`https://www.youtube.com/embed/${img.videoId}`}
                    title={img.alt}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : img.videoType === 'vimeo' && img.vimeoEmbed ? (
                <div 
                  className="w-full aspect-video"
                  dangerouslySetInnerHTML={{ __html: img.vimeoEmbed }}
                />
              ) : null}
            </div>
          </section>
        );
        i += 1;
        continue;
      }

      // Render carousel module
      if (img.type === 'carousel' && img.images) {
        modules.push(
          <section className="w-full py-10" key={`carousel-${i}`} ref={refObjects.current[i]}> 
            <div className="w-full max-w-[1280px] mx-auto px-4 space-y-10">
              <ImageCarousel
                images={img.images}
                className="w-full"
                autoPlay={false}
                showDots={true}
                showArrows={true}
              />
            </div>
          </section>
        );
        i += 1;
        continue;
      }

      // Render text module
      if (img.type === 'text' && img.text) {
        modules.push(
          <section className="w-full py-10" key={`text-${i}`} ref={refObjects.current[i]}>
            <div className="w-full max-w-[800px] mx-auto px-4">
              <BodyText text={img.text} />
            </div>
          </section>
        );
        i += 1;
        continue;
      }
      
      // Render single image module
      modules.push(
        <section className="w-full py-10" key={`single-${i}`} ref={refObjects.current[i]}> 
          <div className="w-full max-w-[1280px] mx-auto px-4 space-y-10">
            <div className="lg:hidden">
              <img 
                src={getImageUrl(img.src)} 
                alt={img.alt} 
                className="w-full"
                loading="eager"
              />
            </div>
            <motion.div
              className="hidden lg:block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.6, 
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
            >
              <img 
                src={getImageUrl(img.src)} 
                alt={img.alt} 
                className="w-full"
                loading="eager"
              />
            </motion.div>
          </div>
        </section>
      );
      i += 1;
    }
    return modules;
  };

  const creativeWorkImage = heroImage ? getImageUrl(heroImage) : getImageUrl('/_images/bb25.jpg');

  const descriptionParagraphs = caseStudyDesc.split('\n\n');
  const experienceLinks = config.experienceLinks
    ?? (config.experienceLink ? [config.experienceLink] : []);
  const imageExperienceLinks = experienceLinks.filter((link) => link.image);
  const experienceSplitIndex = imageExperienceLinks.length
    ? (experienceLinks[0]?.afterParagraph ?? -1) + 1
    : -1;
  const introParagraphs = experienceSplitIndex > 0
    ? descriptionParagraphs.slice(0, experienceSplitIndex)
    : descriptionParagraphs;
  const outroParagraphs = experienceSplitIndex > 0
    ? descriptionParagraphs.slice(experienceSplitIndex)
    : [];
  const pillExperienceLinks = experienceLinks.filter((link) => !link.image);

  const renderPillExperienceLinks = (afterIndex: number): JSX.Element | null => {
    const linksAtIndex = pillExperienceLinks.filter((link) => link.afterParagraph === afterIndex);
    if (!linksAtIndex.length) return null;
    return (
      <div className="py-1 flex flex-wrap gap-3">
        {linksAtIndex.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="inline-flex items-center rounded-full border border-[#0048FF] px-5 py-2 text-sm [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-[#0048FF] hover:bg-[#0048FF] hover:text-white transition-colors duration-200"
          >
            {link.label}
          </a>
        ))}
      </div>
    );
  };

  const renderContactSocials = (): JSX.Element | null => {
    if (!config.contactSocials || config.contactSocials.length === 0) return null;
    return (
      <div className="pt-1 space-y-4 max-w-[600px]">
        <p className="leading-relaxed">Let's talk Word Play — send me a DM.</p>
        <div className="flex flex-wrap gap-4 md:gap-8">
          {config.contactSocials.map((social) => (
            <a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm md:text-base text-black dark:text-white hover:opacity-70 transition-opacity [font-family:'ItemsTextTrial-Light',Helvetica] font-light"
            >
              {social.label}
            </a>
          ))}
        </div>
      </div>
    );
  };

  const hasImageExperienceSection = imageExperienceLinks.length >= 2;

  return (
    <>
      <SEO
        title={caseStudyTitle}
        description={caseStudyDesc.slice(0, 160)}
        url={pageUrl}
        image={heroImage || undefined}
        noindex={noindex}
      />
      {!noindex && heroImage && (
        <JsonLd
          data={getCreativeWorkSchema({
            name: caseStudyTitle,
            description: caseStudyDesc,
            url: pageUrl,
            imageUrl: creativeWorkImage,
            keywords: [caseStudyTitle, 'design', 'creative direction'],
          })}
        />
      )}
    <main className="relative w-full min-h-screen">
      <ClickAnimation 
        isVisible={clickAnimation.visible} 
        position={clickAnimation.position} 
      />
      {/* Fixed gradient background - extends to cover NextProject component */}
      <div className="fixed inset-0 bg-white dark:bg-gradient-to-br dark:from-[#0a0a0a] dark:to-[#2d2d2d] z-50" />
      <div className="relative w-full min-h-screen z-[60] pb-32" ref={contentRef}>
        <Header variant={isHeaderDark ? 'light' : 'dark'} />
        {/* Scroll Indicator - highlight based on in-view modules (only images and videos) */}
        {!isNextProjectVisible && (
          <ScrollIndicator 
            modules={scrollIndicatorModules} 
            containerRef={contentRef} 
            variant={isHeaderDark ? 'light' : 'dark'}
            activeIndices={getHighlightIndices()}
          />
        )}
        {/* Hero Section (first image) */}
        <section className="w-full relative">
          <div className="w-full flex justify-center">
            <motion.div 
              className="w-full lg:max-w-[1280px] xl:max-w-[1440px] aspect-[16/9] overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                ease: [0.25, 0.46, 0.45, 0.94],
                delay: 0.1
              }}
            >
              {caseStudyImages[0].type === 'video' ? (
                <VideoPlayer
                  src={caseStudyImages[0].src}
                  title={caseStudyImages[0].alt}
                  className="w-full h-full"
                  loop={caseStudyImages[0].loop || false}
                  muted={caseStudyImages[0].muted || false}
                  rounded={false}
                  preserveAspectRatio={caseStudyImages[0].preserveAspectRatio || false}
                />
              ) : (
                <img 
                  src={getImageUrl(caseStudyImages[0].src)} 
                  alt={caseStudyImages[0].alt} 
                  className="w-full h-full object-cover object-center"
                  loading="eager"
                />
              )}
            </motion.div>
          </div>
          <motion.div 
            className="w-full max-w-[800px] mx-auto px-4 mb-16 mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: 0.8, 
              duration: 0.8,
              ease: [0.22, 1, 0.36, 1]
            }}
          >
            <h1 className="text-3xl sm:text-4xl lg:text-[64px] [font-family:'ItemsTextTrial-Thin',Helvetica] font-thin text-black dark:text-white mb-8 leading-[1.2] lg:leading-[1.3] tracking-tight">
              {caseStudyTitle}
            </h1>
            <div className="text-sm sm:text-base lg:text-lg [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white opacity-80 leading-relaxed space-y-4">
              {(hasImageExperienceSection ? introParagraphs : descriptionParagraphs).map((paragraph: string, index: number) => (
                <React.Fragment key={`intro-${index}`}>
                  <p className="leading-relaxed max-w-[600px]">{paragraph}</p>
                  {!hasImageExperienceSection && renderPillExperienceLinks(index)}
                </React.Fragment>
              ))}
              {!hasImageExperienceSection && renderContactSocials()}
            </div>
          </motion.div>
        </section>
        {hasImageExperienceSection && (
          <section className="w-full py-10">
            <div className="w-full max-w-[1280px] mx-auto px-4 space-y-10">
              <TwoColumnExperienceLinks
                links={[
                  {
                    label: imageExperienceLinks[0].label,
                    href: imageExperienceLinks[0].href,
                    image: getImageUrl(imageExperienceLinks[0].image!),
                    imageAlt: imageExperienceLinks[0].imageAlt,
                  },
                  {
                    label: imageExperienceLinks[1].label,
                    href: imageExperienceLinks[1].href,
                    image: getImageUrl(imageExperienceLinks[1].image!),
                    imageAlt: imageExperienceLinks[1].imageAlt,
                  },
                ]}
              />
            </div>
          </section>
        )}
        {hasImageExperienceSection && outroParagraphs.length > 0 && (
          <motion.div
            className="w-full max-w-[800px] mx-auto px-4 mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.9,
              duration: 0.8,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <div className="text-sm sm:text-base lg:text-lg [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white opacity-80 leading-relaxed space-y-4">
              {outroParagraphs.map((paragraph: string, index: number) => (
                <p key={`outro-${index}`} className="leading-relaxed max-w-[600px]">
                  {paragraph}
                </p>
              ))}
              {renderContactSocials()}
            </div>
          </motion.div>
        )}
        {/* All image modules rendered from the flat array */}
        {renderImageModules()}
        {/* Credits */}
        {!config.hideCredits && (
        <motion.section 
          className="w-full py-20 px-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ 
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }}
        >
          <div className="max-w-[800px] mx-auto">
            <h2 className="text-sm [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white opacity-60 mb-4">
              {caseStudyCreditsTitle}
            </h2>
            <div className="text-lg [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white opacity-100 space-y-3">
              {(() => {
                const lines = caseStudyCreditsCopy.split('\n');
                const elements: JSX.Element[] = [];
                
                for (let i = 0; i < lines.length; i++) {
                  const line = lines[i];
                  const nextLine = lines[i + 1];
                  
                  // Check if current line is a label and next line contains title and URL
                  const linkPattern = /^(.+?)\s*\((.+?)\)$/;
                  const nextMatch = nextLine ? nextLine.match(linkPattern) : null;
                  
                  if (nextMatch && !line.includes('(') && !line.includes('http')) {
                    // This is a label, next line is title with URL
                    const [, title, url] = nextMatch;
                    elements.push(
                      <div key={i} className="leading-relaxed space-y-1">
                        <div className="opacity-80">{line}</div>
                        <div>
                          <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="underline hover:opacity-70 transition-opacity"
                          >
                            {title}
                          </a>
                        </div>
                      </div>
                    );
                    i++; // Skip next line as it's already processed
                  } else {
                    // Check if line contains a link pattern: Label: Title (url)
                    const inlinePattern = /^(.+?):\s*(.+?)\s*\((.+?)\)$/;
                    const inlineMatch = line.match(inlinePattern);
                    
                    // Check if line contains a simple link pattern: Text (url) without label
                    const simpleLinkPattern = /^(.+?)\s*\((.+?)\)$/;
                    const simpleLinkMatch = line.match(simpleLinkPattern);
                    
                    if (inlineMatch) {
                      const [, label, title, url] = inlineMatch;
                      elements.push(
                        <div key={i} className="leading-relaxed">
                          <span className="opacity-80">{label}: </span>
                          <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="underline hover:opacity-70 transition-opacity"
                          >
                            {title}
                          </a>
                        </div>
                      );
                    } else if (simpleLinkMatch) {
                      const [, text, url] = simpleLinkMatch;
                      elements.push(
                        <div key={i} className="leading-relaxed">
                          <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="underline hover:opacity-70 transition-opacity"
                          >
                            {text}
                          </a>
                        </div>
                      );
                    } else if (line.trim() && !nextMatch) {
                      // Regular text line (only if not empty and not part of a label/title pair)
                      elements.push(
                        <div key={i} className="leading-relaxed">
                          {line}
                        </div>
                      );
                    }
                  }
                }
                
                return elements;
              })()}
            </div>
          </div>
        </motion.section>
        )}
        
        {/* Footer */}
        <footer className="w-full py-8 px-4">
          <div className="max-w-[800px] mx-auto">
          </div>
        </footer>
        
        {/* Next Project - positioned after footer, reveals when scrolling past */}
        <NextProject 
          currentProjectName={caseStudyTitle} 
          projects={PROJECTS} 
          onVisibilityChange={setIsNextProjectVisible}
        />
      </div>
    </main>
    </>
  );
};

// Volvo Cars Configurator Case Study - Specific Instance
const DEFAULT_CREDITS_TITLE = 'Info';
const DEFAULT_CREDITS_COPY = `Volvo Cars`;

const volvoConfig: CaseStudyConfig = {
  storageKey: 'volvo_case_study',
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  modules: DEFAULT_MODULES,
  creditsTitle: DEFAULT_CREDITS_TITLE,
  creditsCopy: DEFAULT_CREDITS_COPY,
  noindex: true,
};

export const VolvoConfigurator = (): JSX.Element => {
  return <CaseStudy config={volvoConfig} />;
};

// Mack Trucks Case Study - Specific Instance
const mackTrucksConfig: CaseStudyConfig = {
  storageKey: 'mack_trucks_case_study',
  title: 'Mack Trucks',
  description: 'The Mack Trucks truck builder represents the next generation of engagement tools, designed to convert prospects into real sales through immersive digital experiences. Built with cutting-edge render-on-demand technology and creative thought, every detail from UI interactions to CGI visuals has been crafted to deliver an authentic and compelling vehicle configuration experience.',
  modules: [
    { src: getImageUrl('/projects/MackTrucks/mack-image1.jpg'), alt: 'Mack Trucks Hero', type: 'single' },
    { src: '', alt: 'Mack Trucks Video', type: 'video', videoType: 'youtube', videoId: 'P852LpkSKx0' },
    { src: getImageUrl('/projects/MackTrucks/mack-image2.jpg'), alt: 'Mack Trucks Overview', type: 'single' },
    { src: getImageUrl('/projects/MackTrucks/mack-image3.jpg'), alt: 'Mack Trucks Detail 1', type: 'single' },
    { src: getImageUrl('/projects/MackTrucks/mack-image4.jpg'), alt: 'Mack Trucks Detail 2', type: 'single' },
    { src: getImageUrl('/projects/MackTrucks/mack-image5.jpg'), alt: 'Mack Trucks Detail 3', type: 'single' },
    { src: getImageUrl('/projects/MackTrucks/mack-image6.jpg'), alt: 'Mack Trucks Detail 4', type: 'single' },
    { src: getImageUrl('/projects/MackTrucks/mack-image7.jpg'), alt: 'Mack Trucks Detail 5', type: 'single' },
  ],
  creditsTitle: 'Info',
  creditsCopy: `Mack Trucks`
};

export const MackTrucks = (): JSX.Element => {
  return <CaseStudy config={mackTrucksConfig} />;
};

// Zeekr Case Study - Specific Instance
const zeekrConfig: CaseStudyConfig = {
  storageKey: 'zeekr_case_study',
  title: 'Zeekr',
  description: 'A cutting-edge electric vehicle platform that redefines the future of mobility through innovative design and advanced technology. Zeekr represents the next generation of premium electric vehicles, combining sustainable engineering with luxury aesthetics to create an unparalleled driving experience.',
  modules: [
    { src: getImageUrl('/projects/Zeekr/zeekr1.jpg'), alt: 'Zeekr Hero', type: 'single' },
    { src: getVideoUrl('zeekr-video.mp4'), alt: 'Zeekr Video', type: 'video', videoType: 'mp4' },
    { src: getImageUrl('/projects/Zeekr/zeekr2.jpg'), alt: 'Zeekr Overview', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr3.jpg'), alt: 'Zeekr Detail 1', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr4.jpg'), alt: 'Zeekr Detail 2', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr5.jpg'), alt: 'Zeekr Detail 3', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr6.jpg'), alt: 'Zeekr Detail 4', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr7.jpg'), alt: 'Zeekr Detail 5', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr8.jpg'), alt: 'Zeekr Detail 6', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr9.jpg'), alt: 'Zeekr Detail 7', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr10.jpg'), alt: 'Zeekr Detail 8', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr2.jpg'), alt: 'Zeekr Overview', type: 'twoColumn', pairIndex: 0 },
    { src: getImageUrl('/projects/Zeekr/zeekr3.jpg'), alt: 'Zeekr Detail 1', type: 'twoColumn', pairIndex: 0 },
    { src: getImageUrl('/projects/Zeekr/zeekr4.jpg'), alt: 'Zeekr Detail 2', type: 'single' },
    { src: getImageUrl('/projects/Zeekr/zeekr5.jpg'), alt: 'Zeekr Detail 3', type: 'twoColumn', pairIndex: 1 },
    { src: getImageUrl('/projects/Zeekr/zeekr6.jpg'), alt: 'Zeekr Detail 4', type: 'twoColumn', pairIndex: 1 },
  ],
  creditsTitle: 'Info',
  creditsCopy: `Zeekr`
};

export const Zeekr = (): JSX.Element => {
  return <CaseStudy config={zeekrConfig} />;
};

// Zeekr Case Study with Automatic Layout Detection
const ZeekrFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateZeekrConfig = async () => {
      try {
        const images: ImageInfo[] = [
          { src: getImageUrl('/projects/Zeekr/zeekr1.jpg'), alt: 'Zeekr Hero' },
          { src: getImageUrl('/projects/Zeekr/zeekr2.jpg'), alt: 'Zeekr Overview' },
          { src: getImageUrl('/projects/Zeekr/zeekr3.jpg'), alt: 'Zeekr Detail 1' },
          { src: getImageUrl('/projects/Zeekr/zeekr4.jpg'), alt: 'Zeekr Detail 2' },
          { src: getImageUrl('/projects/Zeekr/zeekr5.jpg'), alt: 'Zeekr Detail 3' },
          { src: getImageUrl('/projects/Zeekr/zeekr6.jpg'), alt: 'Zeekr Detail 4' },
          { src: getImageUrl('/projects/Zeekr/zeekr7.jpg'), alt: 'Zeekr Detail 5' },
          { src: getImageUrl('/projects/Zeekr/zeekr8.jpg'), alt: 'Zeekr Detail 6' },
          { src: getImageUrl('/projects/Zeekr/zeekr9.jpg'), alt: 'Zeekr Detail 7' },
          { src: getImageUrl('/projects/Zeekr/zeekr10.jpg'), alt: 'Zeekr Detail 8' },
        ];

        const videos: VideoInfo[] = [
          { src: getVideoUrl('zeekr-video.mp4'), alt: 'Zeekr Video', type: 'mp4' }
        ];

        const generatedConfig = await createCaseStudy(
          'zeekr_flexible_case_study',
          'Zeekr',
          'A cutting-edge electric vehicle platform that redefines the future of mobility through innovative design and advanced technology. Zeekr represents the next generation of premium electric vehicles, combining sustainable engineering with luxury aesthetics to create an unparalleled driving experience.',
          images,
          videos,
          1, // Video position after hero image
          'Info',
          'Zeekr'
        );

        setConfig(generatedConfig);
      } catch (error) {
        console.error('Error generating Zeekr config:', error);
        // Fallback to manual config
        setConfig(zeekrConfig);
      } finally {
        setLoading(false);
      }
    };

    generateZeekrConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

// Export the flexible Zeekr component
export { ZeekrFlexible as ZeekrFlexible };

// Volvo Trucks AR Case Study with Automatic Layout Detection
const VolvoTrucksARFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateVolvoTrucksARConfig = async () => {
      try {
        const images: ImageInfo[] = [
          { src: getImageUrl('/projects/VolvoTrucksAR/volvotruckAR-1.jpg'), alt: 'Volvo Trucks AR Hero' },
          { src: getImageUrl('/projects/VolvoTrucksAR/volvotrucksAR-2.jpg'), alt: 'Volvo Trucks AR Overview' },
          { src: getImageUrl('/projects/VolvoTrucksAR/volvotrucksAR-3.jpg'), alt: 'Volvo Trucks AR Detail 1' },
          { src: getImageUrl('/projects/VolvoTrucksAR/volvotrucksAR-4.jpg'), alt: 'Volvo Trucks AR Detail 2' },
        ];

        const videos: VideoInfo[] = [
          { src: getVideoUrl('VolvotrucksAR-video.mp4'), alt: 'Volvo Trucks AR Video', type: 'mp4' }
        ];

        const generatedConfig = await createCaseStudy(
          'volvo_trucks_ar_flexible_case_study',
          'Volvo Trucks AR',
          'An innovative augmented reality experience that brings Volvo Trucks to life through cutting-edge AR technology. This immersive platform allows users to visualize and interact with Volvo\'s commercial vehicles in real-world environments, showcasing advanced features and capabilities through seamless digital integration.',
          images,
          videos,
          1, // Video position after hero image
          'Info',
          'Volvo Trucks'
        );

        setConfig(generatedConfig);
      } catch (error) {
        console.error('Error generating Volvo Trucks AR config:', error);
        // Fallback to manual config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'volvo_trucks_ar_case_study',
          title: 'Volvo Trucks AR',
          description: 'An innovative augmented reality experience that brings Volvo Trucks to life through cutting-edge AR technology.',
          modules: [
            { src: getImageUrl('/projects/VolvoTrucksAR/volvotruckAR-1.jpg'), alt: 'Volvo Trucks AR Hero', type: 'single' },
            { src: getVideoUrl('VolvotrucksAR-video.mp4'), alt: 'Volvo Trucks AR Video', type: 'video', videoType: 'mp4' },
            { src: getImageUrl('/projects/VolvoTrucksAR/volvotrucksAR-2.jpg'), alt: 'Volvo Trucks AR Overview', type: 'single' },
            { src: getImageUrl('/projects/VolvoTrucksAR/volvotrucksAR-3.jpg'), alt: 'Volvo Trucks AR Detail 1', type: 'single' },
            { src: getImageUrl('/projects/VolvoTrucksAR/volvotrucksAR-4.jpg'), alt: 'Volvo Trucks AR Detail 2', type: 'single' },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Volvo Trucks'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateVolvoTrucksARConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

// Export the flexible Volvo Trucks AR component
export { VolvoTrucksARFlexible as VolvoTrucksARFlexible };

// Formula 1 Case Study with Custom Layout (F1-Access video under intro, images, then remaining videos)
const Formula1Flexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateFormula1Config = async () => {
      try {
        // Images for automatic layout detection
        const images: ImageInfo[] = [
          { src: getImageUrl('/projects/Formula1/F1-1.jpg'), alt: 'Formula 1 Hero' },
          { src: getImageUrl('/projects/Formula1/F1-2.jpg'), alt: 'Formula 1 Overview' },
          { src: getImageUrl('/projects/Formula1/F1-3.jpg'), alt: 'Formula 1 Detail 1' },
          { src: getImageUrl('/projects/Formula1/F1-4.jpg'), alt: 'Formula 1 Detail 2' },
          { src: getImageUrl('/projects/Formula1/F1-5.jpg'), alt: 'Formula 1 Detail 3' },
        ];

        // F1-Access video goes under intro (position 1)
        const introVideo: VideoInfo[] = [
          { src: getVideoUrl('F1-Access.mp4'), alt: 'Formula 1 Access Video', type: 'mp4' }
        ];

        // Generate base config with F1-Access video under intro
        const baseConfig = await createCaseStudy(
          'formula1_flexible_case_study',
          'Formula 1: A digital reboot',
          'A cutting-edge digital platform that brings the world of Formula 1 racing to life through immersive technology and innovative design. This comprehensive experience showcases the pinnacle of motorsport engineering, delivering real-time data, interactive features, and behind-the-scenes content that connects fans with the heart of Formula 1 racing.\n\n500 million viewers in 200 territories. A new home, membership service and second-screen experience for one of the world\'s largest sports and entertainment franchises.\n\nThis digital platform was launched in 2014 and continues to this day, with a rebrand having taken place in 2017.',
          images,
          introVideo,
          1, // F1-Access video position after hero image
          'Info',
          'Formula 1'
        );

        // Add F1-TV-Graphics.mp4 after F1-2.jpg (position 3)
        const tvGraphicsVideo: CaseStudyConfig['modules'] = [
          { src: getVideoUrl('F1-TV-Graphics.mp4'), alt: 'Formula 1 TV Graphics', type: 'video', videoType: 'mp4' }
        ];

        // Add remaining videos at the end
        const remainingVideos: CaseStudyConfig['modules'] = [
          { src: getVideoUrl('F1-ui1.mp4'), alt: 'Formula 1 UI Demo 1', type: 'video', videoType: 'mp4' },
          { src: getVideoUrl('F1-ui2.mp4'), alt: 'Formula 1 UI Demo 2', type: 'video', videoType: 'mp4' },
        ];

        // Combine base modules with TV Graphics video after F1-2.jpg and remaining videos at the end
        const finalModules = [...baseConfig.modules];
        
        // Insert F1-TV-Graphics.mp4 after F1-2.jpg (find the position of F1-2.jpg and insert after it)
        const f1TwoIndex = finalModules.findIndex(module => module.src.includes('F1-2.jpg'));
        if (f1TwoIndex !== -1) {
          finalModules.splice(f1TwoIndex + 1, 0, ...tvGraphicsVideo);
        }

        // Add remaining videos at the end
        finalModules.push(...remainingVideos);

        const finalConfig: CaseStudyConfig = {
          ...baseConfig,
          modules: finalModules
        };

        setConfig(finalConfig);
      } catch (error) {
        console.error('Error generating Formula 1 config:', error);
        // Fallback to manual config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'formula1_case_study',
          title: 'Formula 1: A digital reboot',
          description: 'A cutting-edge digital platform that brings the world of Formula 1 racing to life through immersive technology and innovative design. This comprehensive experience showcases the pinnacle of motorsport engineering, delivering real-time data, interactive features, and behind-the-scenes content that connects fans with the heart of Formula 1 racing.\n\n500 million viewers in 200 territories. A new home, membership service and second-screen experience for one of the world\'s largest sports and entertainment franchises.\n\nThis digital platform was launched in 2014 and continues to this day, with a rebrand having taken place in 2017.',
          modules: [
            { src: getImageUrl('/projects/Formula1/F1-1.jpg'), alt: 'Formula 1 Hero', type: 'single' },
            { src: getVideoUrl('F1-Access.mp4'), alt: 'Formula 1 Access Video', type: 'video', videoType: 'mp4' },
            { src: getImageUrl('/projects/Formula1/F1-2.jpg'), alt: 'Formula 1 Overview', type: 'single' },
            { src: getVideoUrl('F1-TV-Graphics.mp4'), alt: 'Formula 1 TV Graphics', type: 'video', videoType: 'mp4' },
            { src: getImageUrl('/projects/Formula1/F1-3.jpg'), alt: 'Formula 1 Detail 1', type: 'single' },
            { src: getImageUrl('/projects/Formula1/F1-4.jpg'), alt: 'Formula 1 Detail 2', type: 'single' },
            { src: getImageUrl('/projects/Formula1/F1-5.jpg'), alt: 'Formula 1 Detail 3', type: 'single' },
            { src: getVideoUrl('F1-ui1.mp4'), alt: 'Formula 1 UI Demo 1', type: 'video', videoType: 'mp4' },
            { src: getVideoUrl('F1-ui2.mp4'), alt: 'Formula 1 UI Demo 2', type: 'video', videoType: 'mp4' },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Formula 1'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateFormula1Config();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

/**
 * Nike Case Study Component
 * Uses custom configuration with carousel for PR images
 */
const NikeFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateNikeConfig = async () => {
      try {
        // Create carousel images array
        const carouselImages = [
          { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-PR-1.jpeg'), alt: 'Nike PR Image 1' },
          { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-PR-2.jpeg'), alt: 'Nike PR Image 2' },
          { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-PR-3.jpeg'), alt: 'Nike PR Image 3' },
          { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-PR-4.jpeg'), alt: 'Nike PR Image 4' },
          { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-PR-5.jpeg'), alt: 'Nike PR Image 5' },
          { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-PR-6.jpeg'), alt: 'Nike PR Image 6' },
        ];

        const nikeConfig: CaseStudyConfig = {
          storageKey: 'nike_case_study',
          title: 'Nike Tech Pack',
          description: 'A revolutionary digital experience that showcases Nike\'s latest technical innovations and performance technologies. This comprehensive platform brings together cutting-edge design, interactive storytelling, and immersive content to highlight Nike\'s commitment to pushing the boundaries of athletic performance and style.\n\nThrough innovative digital storytelling and interactive experiences, this project demonstrates how technology and creativity converge to create compelling brand narratives that resonate with athletes and enthusiasts worldwide.',
          modules: [
            { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-1.jpeg'), alt: 'Nike TCP Hero', type: 'single' },
            { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-2.jpeg'), alt: 'Nike TCP Overview', type: 'single' },
            { 
              src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-PR-1.jpeg'), 
              alt: 'Nike PR Gallery', 
              type: 'carousel',
              images: carouselImages
            },
            { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-3.jpeg'), alt: 'Nike TCP Detail', type: 'single' },
            { src: getVideoUrl('TCP-ads1.mp4'), alt: 'Nike TCP Ad 1', type: 'video', videoType: 'mp4' },
            { src: getVideoUrl('TCP-ads2.mp4'), alt: 'Nike TCP Ad 2', type: 'video', videoType: 'mp4' },
            { src: getVideoUrl('TCP-movie.mp4'), alt: 'Nike TCP Movie', type: 'video', videoType: 'mp4' },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Nike'
        };

        setConfig(nikeConfig);
      } catch (error) {
        console.error('Error generating Nike config:', error);
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'nike_case_study',
          title: 'Nike Tech Pack',
          description: 'A revolutionary digital experience that showcases Nike\'s latest technical innovations and performance technologies.',
          modules: [
            { src: getImageUrl('/projects/Nike/NIKE-TCP-1/NIKE-1.jpeg'), alt: 'Nike TCP Hero', type: 'single' },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Nike'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateNikeConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

// Export the flexible Nike component
export { NikeFlexible as NikeFlexible };

// Nissan IDx Case Study Component
const NissanDxFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateNissanConfig = async () => {
      try {
        // Create carousel images array
        const carouselImages = [
          { src: getImageUrl('/projects/Nissan/Nissan-4-Gallery1.png'), alt: 'Nissan IDx Gallery 1' },
          { src: getImageUrl('/projects/Nissan/Nissan-4-Gallery2.png'), alt: 'Nissan IDx Gallery 2' },
          { src: getImageUrl('/projects/Nissan/Nissan-4-Gallery3.png'), alt: 'Nissan IDx Gallery 3' },
        ];

        const nissanConfig: CaseStudyConfig = {
          storageKey: 'nissan_idx_case_study',
          title: 'Nissan IDx',
          description: 'An innovative digital platform showcasing Nissan\'s cutting-edge design philosophy and technological advancements. This comprehensive experience brings together interactive storytelling, immersive content, and dynamic visualizations to highlight Nissan\'s commitment to innovation and user-centric design.\n\nThrough sophisticated digital interfaces and engaging multimedia content, this project demonstrates how automotive design meets digital innovation to create compelling brand experiences that resonate with modern consumers.',
          modules: [
            { src: getImageUrl('/projects/Nissan/Nissan-1.png'), alt: 'Nissan IDx Hero', type: 'single' },
            { src: getImageUrl('/projects/Nissan/Nissan-2.png'), alt: 'Nissan IDx Overview', type: 'single' },
            {
              src: getImageUrl('/projects/Nissan/Nissan-4-Gallery1.png'),
              alt: 'Nissan IDx Gallery',
              type: 'carousel',
              images: carouselImages
            },
            { src: getImageUrl('/projects/Nissan/Nissan-3.png'), alt: 'Nissan IDx Detail', type: 'single' },
            { src: getImageUrl('/projects/Nissan/Nissan-5.png'), alt: 'Nissan IDx Features', type: 'single' },
            { 
              src: '', 
              alt: 'Nissan IDx VR Description', 
              type: 'text',
              text: 'At the 2013 Tokyo Motor Show, we launched the world\'s first VR configurator in the automotive industry. It was powered by Oculus Rift and gaze-based interaction, letting participants shape custom IDx designs in real time. Queues peaked at 70 minutes and guests spent an average of 7 minutes immersed in the experience. With multiple VR stations, creating became the most time-consuming activity at Nissan\'s booth. To top it off, Nissan committed to bringing the most popular user-generated designs to life as concept cars, turning this not just into a spectacle but a real collaborative design exchange.'
            },
            { src: getImageUrl('/projects/Nissan/Nissan-6.jpg'), alt: 'Nissan IDx Final', type: 'single' },
            { 
              src: getVideoUrl('Nissan-IDx-VR.mp4'), 
              alt: 'Nissan IDx VR Experience', 
              type: 'video', 
              videoType: 'mp4'
            },
            { 
              src: getVideoUrl('nissan_idx_ui_1.mp4'), 
              alt: 'Nissan IDx UI Animation 1', 
              type: 'video', 
              videoType: 'mp4'
            },
            { 
              src: getVideoUrl('nissan_idx_ui_2.mp4'), 
              alt: 'Nissan IDx UI Animation 2', 
              type: 'video', 
              videoType: 'mp4'
            },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Nissan'
        };

        setConfig(nissanConfig);
      } catch (error) {
        console.error('Error generating Nissan config:', error);
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'nissan_idx_case_study',
          title: 'Nissan IDx',
          description: 'An innovative digital platform showcasing Nissan\'s cutting-edge design philosophy and technological advancements.',
          modules: [
            { src: getImageUrl('/projects/Nissan/Nissan-1.png'), alt: 'Nissan IDx Hero', type: 'single' },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Nissan'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateNissanConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

export { NissanDxFlexible as NissanDxFlexible };

// Volvo Trucks Aero Case Study Component
const VolvoTrucksAeroFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateVolvoAeroConfig = async () => {
      try {
        // Create carousel images array
        const carouselImages = [
          { src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.002.jpeg'), alt: 'Volvo Aero Pres 2' },
          { src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.003.jpeg'), alt: 'Volvo Aero Pres 3' },
          { src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.004.jpeg'), alt: 'Volvo Aero Pres 4' },
          { src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.005.jpeg'), alt: 'Volvo Aero Pres 5' },
          { src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.006.jpeg'), alt: 'Volvo Aero Pres 6' },
          { src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.007.jpeg'), alt: 'Volvo Aero Pres 7' },
          { src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.008.jpeg'), alt: 'Volvo Aero Pres 8' },
          { src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.009.jpeg'), alt: 'Volvo Aero Pres 9' },
          { src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.010.jpeg'), alt: 'Volvo Aero Pres 10' },
        ];

        const volvoAeroConfig: CaseStudyConfig = {
          storageKey: 'volvo_trucks_aero_case_study',
          title: 'Volvo Trucks Aero',
          description: 'To launch the new Volvo Aero range, we set out to break the formula of every truck reveal from the past decade. Instead of long speeches and slow unveilings, we built anticipation by turning the truck\'s name into the campaign itself. Over two weeks, Volvo\'s channels teased one letter at a time, sparking huge engagement as audiences speculated on what "A-E-R-O" could mean.\n\nThen, in under a minute, the launch film delivered the reveal: not just one truck, but an entire family. A faster, more digital-native way to introduce the Volvo Aero to the world.',
          modules: [
            { src: getVideoUrl('AERO_Final_loop_small.mp4'), alt: 'Volvo Aero Hero Video', type: 'video', videoType: 'mp4', loop: true },
            { src: getVideoUrl('AERO-Reveal-Film.mp4'), alt: 'Volvo Aero Reveal Film', type: 'video', videoType: 'mp4' },
            { src: getImageUrl('/projects/VolvoTrucksAero/AERO-LooknFeel1.jpg'), alt: 'Volvo Aero Look & Feel 1', type: 'twoColumn', pairIndex: 0 },
            { src: getImageUrl('/projects/VolvoTrucksAero/AERO-LooknFeel2.jpg'), alt: 'Volvo Aero Look & Feel 2', type: 'twoColumn', pairIndex: 0 },
            { src: getImageUrl('/projects/VolvoTrucksAero/AERO-LooknFeel3.jpg'), alt: 'Volvo Aero Look & Feel 3', type: 'twoColumn', pairIndex: 1 },
            { src: getImageUrl('/projects/VolvoTrucksAero/AERO-LooknFeel4.jpg'), alt: 'Volvo Aero Look & Feel 4', type: 'twoColumn', pairIndex: 1 },
            {
              src: getImageUrl('/projects/VolvoTrucksAero/AERO-pres/AERO.001.jpeg'),
              alt: 'Volvo Aero Pres Gallery',
              type: 'carousel',
              images: carouselImages
            },
                     { src: getImageUrl('/projects/VolvoTrucksAero/AERO-Styleframes.jpg'), alt: 'Volvo Aero Styleframes', type: 'single' },
                     { src: getVideoUrl('AERO-Web.mp4'), alt: 'Volvo Aero Web Video', type: 'video', videoType: 'mp4' },
                     { src: getVideoUrl('A.mp4'), alt: 'Volvo Aero A Video', type: 'video', videoType: 'mp4', pairIndex: 2, loop: true },
                     { src: getVideoUrl('E.mp4'), alt: 'Volvo Aero E Video', type: 'video', videoType: 'mp4', pairIndex: 2, loop: true },
                     { src: getVideoUrl('R.mp4'), alt: 'Volvo Aero R Video', type: 'video', videoType: 'mp4', pairIndex: 3, loop: true },
                     { src: getVideoUrl('O.mp4'), alt: 'Volvo Aero O Video', type: 'video', videoType: 'mp4', pairIndex: 3, loop: true },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Volvo Trucks'
        };

        setConfig(volvoAeroConfig);
      } catch (error) {
        console.error('Error generating Volvo Aero config:', error);
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'volvo_trucks_aero_case_study',
          title: 'Volvo Trucks Aero',
          description: 'To launch the new Volvo Aero range, we set out to break the formula of every truck reveal from the past decade.',
          modules: [
            { src: getVideoUrl('AERO_Final_loop_small.mp4'), alt: 'Volvo Aero Hero Video', type: 'video', videoType: 'mp4', loop: true },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Volvo Trucks'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateVolvoAeroConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

export { VolvoTrucksAeroFlexible as VolvoTrucksAeroFlexible };

// Nivea Flexible Case Study Component
const NiveaFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateNiveaConfig = async () => {
      try {
        const niveaConfig: CaseStudyConfig = {
          storageKey: 'nivea_case_study',
          title: 'Nivea',
          description: 'Nivea Naturally Good campaign celebrates the beauty of natural ingredients and authentic moments. This campaign showcases how Nivea\'s products enhance natural beauty while staying true to their heritage of care and quality.',
          modules: [
            { src: getImageUrl('/projects/Nivea/NIVEA-1.jpg'), alt: 'Nivea Hero Image', type: 'single' },
            { src: getVideoUrl('NIVEA_Naturally_Good.mp4'), alt: 'Nivea Naturally Good Video', type: 'video', videoType: 'mp4' },
            { src: getImageUrl('/projects/Nivea/NIVEA-2.jpg'), alt: 'Nivea Image 2', type: 'twoColumn', pairIndex: 0 },
            { src: getImageUrl('/projects/Nivea/NIVEA-3.jpg'), alt: 'Nivea Image 3', type: 'twoColumn', pairIndex: 0 },
            { src: getImageUrl('/projects/Nivea/NIVEA-4.jpg'), alt: 'Nivea Image 4', type: 'twoColumn', pairIndex: 1 },
            { src: getImageUrl('/projects/Nivea/NIVEA-5.jpg'), alt: 'Nivea Image 5', type: 'twoColumn', pairIndex: 1 },
            { src: getImageUrl('/projects/Nivea/NIVEA-6.jpg'), alt: 'Nivea Image 6', type: 'single' },
            { src: getImageUrl('/projects/Nivea/NIVEA_7.jpg'), alt: 'Nivea Image 7', type: 'twoColumn', pairIndex: 2 },
            { src: getImageUrl('/projects/Nivea/NIVEA_8.jpg'), alt: 'Nivea Image 8', type: 'twoColumn', pairIndex: 2 },
            { src: getImageUrl('/projects/Nivea/NIVEA_9.jpg'), alt: 'Nivea Image 9', type: 'single' },
            { src: getVideoUrl('Nivea-BTS.mp4'), alt: 'Nivea Behind the Scenes', type: 'video', videoType: 'mp4', muted: true },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Nivea'
        };

        setConfig(niveaConfig);
      } catch (error) {
        console.error('Error generating Nivea config:', error);
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'nivea_case_study',
          title: 'Nivea',
          description: 'Nivea Naturally Good campaign celebrates the beauty of natural ingredients.',
          modules: [
            { src: getImageUrl('/projects/Nivea/NIVEA-1.jpg'), alt: 'Nivea Hero Image', type: 'single' },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Nivea'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateNiveaConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

export { NiveaFlexible as NiveaFlexible };

// Dieter Rams Flexible Case Study Component
const DieterRamsFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateDieterRamsConfig = async () => {
      try {
        const dieterRamsConfig: CaseStudyConfig = {
          storageKey: 'dieter_rams_case_study',
          title: 'Dieter Rams',
          description: 'In partnership with Wallpaper magazine, AKQA premiered Gary Hustwit\'s documentary on Dieter Rams at the Barbican in London. I conceptualised the promotional materials that introduced the film to its UK audience and led the team in bringing that vision to life — from idea to execution.',
          modules: [
            { src: getVideoUrl('RAMS.mp4'), alt: 'Dieter Rams Hero Video', type: 'video', videoType: 'mp4', loop: true },
            { src: getVideoUrl('Diter_TapePlayer.mp4'), alt: 'Dieter Rams Tape Player', type: 'video', videoType: 'mp4', pairIndex: 0, loop: true },
            { src: getVideoUrl('diter-TV.mp4'), alt: 'Dieter Rams TV', type: 'video', videoType: 'mp4', pairIndex: 1, loop: true },
            { src: getVideoUrl('diter-amplifier.mp4'), alt: 'Dieter Rams Amplifier', type: 'video', videoType: 'mp4', pairIndex: 1, loop: true },
            { src: getVideoUrl('diter-camera.mp4'), alt: 'Dieter Rams Camera', type: 'video', videoType: 'mp4', pairIndex: 2, loop: true },
            { src: getVideoUrl('diter-filmroll.mp4'), alt: 'Dieter Rams Film Roll', type: 'video', videoType: 'mp4', pairIndex: 2, loop: true },
            { src: getVideoUrl('diter-radio2.mp4'), alt: 'Dieter Rams Radio 2', type: 'video', videoType: 'mp4', pairIndex: 3, loop: true },
            { src: getVideoUrl('diter-radio3.mp4'), alt: 'Dieter Rams Radio 3', type: 'video', videoType: 'mp4', pairIndex: 3, loop: true },
            { src: getVideoUrl('diter-vectors_Calculator.mp4'), alt: 'Dieter Rams Calculator', type: 'video', videoType: 'mp4', pairIndex: 4, loop: true },
            { src: getVideoUrl('diter-vinyl.mp4'), alt: 'Dieter Rams Vinyl', type: 'video', videoType: 'mp4', pairIndex: 4, loop: true },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Dieter Rams'
        };

        setConfig(dieterRamsConfig);
      } catch (error) {
        console.error('Error generating Dieter Rams config:', error);
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'dieter_rams_case_study',
          title: 'Dieter Rams',
          description: 'In partnership with Wallpaper magazine, AKQA premiered Gary Hustwit\'s documentary on Dieter Rams at the Barbican in London.',
          modules: [
            { src: getVideoUrl('RAMS.mp4'), alt: 'Dieter Rams Hero Video', type: 'video', videoType: 'mp4', loop: true },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Dieter Rams'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateDieterRamsConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

export { DieterRamsFlexible as DieterRamsFlexible };

// Export the flexible Formula 1 component
export { Formula1Flexible as Formula1Flexible };

// AI Playground Case Study
const AIPlaygroundFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateAIPlaygroundConfig = async () => {
      try {
        // Images for AI Playground (hero from S3, then previous hero moved down, then rest)
        const images: ImageInfo[] = [
          { src: getImageUrl('/_images/AI-Playground/BB-Porsche-Spreads.jpg'), alt: 'AI Playground Hero' },
          { src: getImageUrl('/_images/AI-Playground/Porsche-Mockup.jpg'), alt: 'AI Playground Porsche Mockup' },
          { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan1-logo.jpg'), alt: 'AI Playground Porsche Taycan Logo' },
          { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan2-Crest.jpg'), alt: 'AI Playground Porsche Taycan Crest' },
          { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan3.jpg'), alt: 'AI Playground Porsche Taycan 3' },
          { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan4.jpg'), alt: 'AI Playground Porsche Taycan 4' },
          { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan5.jpg'), alt: 'AI Playground Porsche Taycan 5' },
          { src: getImageUrl('/projects/AI-Playground/Volvo-01.jpg'), alt: 'AI Playground Volvo 01' },
          { src: getImageUrl('/projects/AI-Playground/Volvo-02.jpg'), alt: 'AI Playground Volvo 02' },
          { src: getImageUrl('/projects/AI-Playground/ps1.png'), alt: 'AI Playground PS1' },
          { src: getImageUrl('/projects/AI-Playground/ps2.png'), alt: 'AI Playground PS2' },
          { src: getImageUrl('/projects/AI-Playground/ps3.png'), alt: 'AI Playground PS3' },
          { src: getImageUrl('/projects/AI-Playground/ps4.png'), alt: 'AI Playground PS4' },
          { src: getImageUrl('/projects/AI-Playground/ps5.png'), alt: 'AI Playground PS5' },
        ];

        // Generate config using the flexible system
        const generatedConfig = await createCaseStudy(
          'ai_playground_case_study',
          'AI Playground',
          'The AI Playground is a space where I explore the creative potential of artificial intelligence. A collection of experiments, prototypes, and visual studies that push the boundaries of design and automation in new and unexpected ways.\n\nThis portfolio was designed in Figma and built using Cursor, as part of an ongoing exploration into how emerging tools are reshaping the role of design.',
          images,
          [], // No videos for now
          1, // No video position needed
          'Info',
          'AI Playground'
        );

        setConfig(generatedConfig);
      } catch (error) {
        console.error('Error generating AI Playground config:', error);
        
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'ai_playground_case_study',
          title: 'AI Playground',
          description: 'An innovative exploration of artificial intelligence in automotive design and visualization.',
          modules: [
            { src: getImageUrl('/_images/AI-Playground/BB-Porsche-Spreads.jpg'), alt: 'AI Playground Hero', type: 'single' },
            { src: getImageUrl('/_images/AI-Playground/Porsche-Mockup.jpg'), alt: 'AI Playground Porsche Mockup', type: 'single' },
            { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan1-logo.jpg'), alt: 'AI Playground Porsche Taycan Logo', type: 'single' },
            { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan2-Crest.jpg'), alt: 'AI Playground Porsche Taycan Crest', type: 'single' },
            { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan3.jpg'), alt: 'AI Playground Porsche Taycan 3', type: 'twoColumn', pairIndex: 0 },
            { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan4.jpg'), alt: 'AI Playground Porsche Taycan 4', type: 'twoColumn', pairIndex: 0 },
            { src: getImageUrl('/_images/AI-Playground/Porsche-Tycan5.jpg'), alt: 'AI Playground Porsche Taycan 5', type: 'single' },
            { src: getImageUrl('/projects/AI-Playground/Volvo-01.jpg'), alt: 'AI Playground Volvo 01', type: 'single' },
            { src: getImageUrl('/projects/AI-Playground/Volvo-02.jpg'), alt: 'AI Playground Volvo 02', type: 'single' },
            { src: getImageUrl('/projects/AI-Playground/ps1.png'), alt: 'AI Playground PS1', type: 'single' },
            { src: getImageUrl('/projects/AI-Playground/ps2.png'), alt: 'AI Playground PS2', type: 'single' },
            { src: getImageUrl('/projects/AI-Playground/ps3.png'), alt: 'AI Playground PS3', type: 'single' },
            { src: getImageUrl('/projects/AI-Playground/ps4.png'), alt: 'AI Playground PS4', type: 'single' },
            { src: getImageUrl('/projects/AI-Playground/ps5.png'), alt: 'AI Playground PS5', type: 'single' },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'AI Playground'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateAIPlaygroundConfig();
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading</div>;
  }

  if (!config) {
    return <div>Error loading AI Playground case study</div>;
  }

  return <CaseStudy config={config} />;
};

// Export the flexible AI Playground component
export { AIPlaygroundFlexible as AIPlaygroundFlexible };

// Volvo Trucks NA Case Study Component
const VolvoTrucksNAFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateVolvoTrucksNAConfig = async () => {
      try {
        // Create materials carousel
        const materialsCarouselImages = [
          { src: getImageUrl('/projects/VolvoTrucksNA/materials.001.jpeg'), alt: 'Volvo Trucks NA Materials 1' },
          { src: getImageUrl('/projects/VolvoTrucksNA/materials.002.jpeg'), alt: 'Volvo Trucks NA Materials 2' },
          { src: getImageUrl('/projects/VolvoTrucksNA/materials.003.jpeg'), alt: 'Volvo Trucks NA Materials 3' },
          { src: getImageUrl('/projects/VolvoTrucksNA/Materials.png'), alt: 'Volvo Trucks NA Materials Overview' },
        ];

        // Create shoot images carousel
        const shootCarouselImages = [
          { src: getImageUrl('/projects/VolvoTrucksNA/Shoot1.jpeg'), alt: 'Volvo Trucks NA Shoot 1' },
          { src: getImageUrl('/projects/VolvoTrucksNA/Shoot2.jpeg'), alt: 'Volvo Trucks NA Shoot 2' },
          { src: getImageUrl('/projects/VolvoTrucksNA/Shoot3.jpeg'), alt: 'Volvo Trucks NA Shoot 3' },
          { src: getImageUrl('/projects/VolvoTrucksNA/Shoot4.png'), alt: 'Volvo Trucks NA Shoot 4' },
        ];

        const volvoTrucksNAConfig: CaseStudyConfig = {
          storageKey: 'volvo_trucks_na_case_study',
          title: 'Volvo Trucks North America',
          description: 'Volvo Trucks set out to redefine how customers explore and configure their heavy-duty vehicles online. What began as a request for a new configurator evolved into a digital twin ecosystem that transformed how Volvo designs, markets, and sells its trucks in the U.S. market.',
          modules: [
            // Hero
            { src: getImageUrl('/projects/VolvoTrucksNA/Hero.jpg'), alt: 'Volvo Trucks NA Hero', type: 'single' },
            
            // Digital twin/configurator visuals - twoColumn pair
            { src: getImageUrl('/projects/VolvoTrucksNA/Studio.jpeg'), alt: 'Volvo Trucks NA Studio Configurator', type: 'twoColumn', pairIndex: 0 },
            { src: getImageUrl('/projects/VolvoTrucksNA/Interior.jpeg'), alt: 'Volvo Trucks NA Interior', type: 'twoColumn', pairIndex: 0 },
            
            // Background text
            {
              src: '',
              alt: 'Background',
              type: 'text',
              text: 'Background\n\nAfter years of traditional CGI production, Volvo Trucks North America wanted a faster, smarter way to create and deploy visual experiences. They needed a configurator that could handle thousands of truck variations while maintaining world-class visual quality and responsiveness. Our earlier experiments in real-time rendering had proven that Unreal Engine could meet those demands. We proposed building a digital twin of the trucks that could extend beyond the configurator, powering education, training, virtual production, and marketing content from a single source.'
            },
            
            // Main video - moved up after Background text
            { src: getImageUrl('/projects/VolvoTrucksNA/volvotrucksna.mov'), alt: 'Volvo Trucks NA Main Video', type: 'video', videoType: 'mp4' },
            
            // Environment images - Nevada and Miami
            { src: getImageUrl('/projects/VolvoTrucksNA/VNL-Desert-1.jpeg'), alt: 'Volvo Trucks NA Nevada Environment', type: 'twoColumn', pairIndex: 1 },
            { src: getImageUrl('/projects/VolvoTrucksNA/vnr-miami.jpeg'), alt: 'Volvo Trucks NA Miami Environment', type: 'twoColumn', pairIndex: 1 },
            
            // What We Did text
            {
              src: '',
              alt: 'What We Did',
              type: 'text',
              text: 'What We Did\n\nWe partnered closely with Volvo\'s product and marketing teams to build a full digital twin pipeline using Unreal Engine and our OneCX platform.\n\n– Captured real-world environments in Nevada\'s dry lake beds and Miami\'s waterfront to integrate authentic lighting and reflections.\n\n– Produced ultra-high-fidelity textures, materials, and HDRI domes for seamless parity between CGI and photography.\n\n– Developed a real-time configurator capable of visualizing over 7,000 options, including interior trims, cabins, chassis, and accessories, all running on a single GPU.\n\n– Enabled reuse of the same digital assets across Unreal and V-Ray for marketing films, print, and social campaigns, ensuring visual consistency across every channel.'
            },
            
            // Materials carousel
            {
              src: getImageUrl('/projects/VolvoTrucksNA/materials.001.jpeg'),
              alt: 'Volvo Trucks NA Materials',
              type: 'carousel',
              images: materialsCarouselImages
            },
            
            // Top view and reveal
            { src: getImageUrl('/projects/VolvoTrucksNA/vnr-top-view.jpeg'), alt: 'Volvo Trucks NA Top View', type: 'single' },
            { src: getImageUrl('/projects/VolvoTrucksNA/VTNA-reveal.jpeg'), alt: 'Volvo Trucks NA Reveal', type: 'single' },
            
            // Videos - using getImageUrl since videos are in _images folder
            { src: getImageUrl('/projects/VolvoTrucksNA/Digital-Twin.mov'), alt: 'Volvo Trucks NA Digital Twin', type: 'video', videoType: 'mp4' },
            { src: getImageUrl('/projects/VolvoTrucksNA/T-builder-mobile.mp4'), alt: 'Volvo Trucks NA T-Builder Mobile', type: 'video', videoType: 'mp4', pairIndex: 0, loop: true, preserveAspectRatio: true },
            { src: getImageUrl('/projects/VolvoTrucksNA/T-builder-2.mov'), alt: 'Volvo Trucks NA T-Builder 2', type: 'video', videoType: 'mp4', pairIndex: 0 },
            
            // Shoot carousel
            {
              src: getImageUrl('/projects/VolvoTrucksNA/Shoot1.jpeg'),
              alt: 'Volvo Trucks NA Production Shoot',
              type: 'carousel',
              images: shootCarouselImages
            },
            
            // Launch image
            { src: getImageUrl('/projects/VolvoTrucksNA/launch2.jpeg'), alt: 'Volvo Trucks NA Launch', type: 'single' },
            
            // Results text
            {
              src: '',
              alt: 'Results',
              type: 'text',
              text: 'Results\n\nThe impact was immediate and measurable:\n\n– 991% increase in configurations compared to the previous configurator\n\n– 12-minute average session times\n\n– Unreal-based production exceeded expectations in visual quality, performance, and cost efficiency\n\n– The digital twin pipeline became a foundation for future launches, scalable across models, markets, and media'
            },
          ],
          creditsTitle: 'Press',
          creditsCopy: `Adobe Blog
Volvo Trucks accelerates digital twin production with Substance 3D (https://blog.adobe.com/en/publish/2025/12/02/volvo-truck-accelerates-digital-twin-production-substance-3d)`
        };

        setConfig(volvoTrucksNAConfig);
      } catch (error) {
        console.error('Error generating Volvo Trucks NA config:', error);
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'volvo_trucks_na_case_study',
          title: 'Volvo Trucks North America',
          description: 'Volvo Trucks set out to redefine how customers explore and configure their heavy-duty vehicles online.',
          modules: [
            { src: getImageUrl('/projects/VolvoTrucksNA/Hero.jpg'), alt: 'Volvo Trucks NA Hero', type: 'single' },
          ],
          creditsTitle: 'Press',
          creditsCopy: `Adobe Blog
Volvo Trucks accelerates digital twin production with Substance 3D (https://blog.adobe.com/en/publish/2025/12/02/volvo-truck-accelerates-digital-twin-production-substance-3d)`
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateVolvoTrucksNAConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

// Export the flexible Volvo Trucks NA component
export { VolvoTrucksNAFlexible as VolvoTrucksNAFlexible };

// Volvo Rider Flexible Case Study Component
const VolvoRiderFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateVolvoRiderConfig = async () => {
      try {
        const volvoRiderConfig: CaseStudyConfig = {
          storageKey: 'volvo_rider_case_study',
          title: 'Volvo Rider',
          description: 'Volvo Rider reimagined a humble lifestyle accessory as a full-blown automotive launch. By taking a small toy car from Volvo\'s accessories range and treating it like the next breakthrough in autonomous driving, we created a tongue-in-cheek campaign that borrowed all the visual language of a premium car reveal. Cinematic lighting, dramatic close-ups, confident launch copy and serious intent were used to sell something intentionally unserious. The result was a playful cultural hack that tapped into the hype around self-driving technology, surprised audiences, and turned an overlooked product into a highly shareable brand moment that drove attention, conversation, and traffic far beyond expectations.',
          noindex: true,
          modules: [
            { src: getImageUrl('/projects/Volvo-Rider/VR-001.jpg'), alt: 'Volvo Rider Hero Image', type: 'single' },
            { src: getVideoUrl('Volvo_Rider_BB.mp4'), alt: 'Volvo Rider Video', type: 'video', videoType: 'mp4' },
            { src: getImageUrl('/projects/Volvo-Rider/VR-002.jpg'), alt: 'Volvo Rider Image 2', type: 'twoColumn', pairIndex: 0 },
            { src: getImageUrl('/projects/Volvo-Rider/VR-003.jpg'), alt: 'Volvo Rider Image 3', type: 'twoColumn', pairIndex: 0 },
            { 
              type: 'text',
              text: '**Challenge**\n\nAccessories rarely get attention. Volvo needed a way to make a low-interest product feel relevant, desirable, and worth talking about.\n\n**Opportunity**\n\nPublic fascination with autonomous driving was peaking, creating the perfect moment to hijack the language and theatrics of next-gen car launches.\n\n**Impact**\n\nThe film earned organic coverage across major automotive and tech media and drove an almost 8,000 percent spike in social traffic to Volvo\'s accessories site within minutes of launch. A small product delivered outsized attention by turning smart humor and cultural timing into real business impact.'
            },
            { src: getImageUrl('/projects/Volvo-Rider/VR-003.jpg'), alt: 'Volvo Rider Image 3', type: 'single' },
            { src: getImageUrl('/projects/Volvo-Rider/VR-004.jpg'), alt: 'Volvo Rider Image 4', type: 'single' },
            { src: getImageUrl('/projects/Volvo-Rider/VR-005.jpg'), alt: 'Volvo Rider Image 5', type: 'single' },
            { src: getImageUrl('/projects/Volvo-Rider/VR-006.jpg'), alt: 'Volvo Rider Image 6', type: 'single' },
            { src: getImageUrl('/projects/Volvo-Rider/VR-007.jpg'), alt: 'Volvo Rider Image 7', type: 'single' },
          ],
          creditsTitle: 'Press',
          creditsCopy: `The Drive
Volvo Teases Their Smallest Coupe Ever (https://www.thedrive.com/news/9055/volvo-teases-their-smallest-coupe-ever)
Jalopnik
The New Volvo Rider Is A Freaking Packaging Nightmare (https://www.jalopnik.com/the-new-volvo-rider-is-a-freaking-packaging-nightmare-1794095785/)`
        };

        setConfig(volvoRiderConfig);
      } catch (error) {
        console.error('Error generating Volvo Rider config:', error);
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'volvo_rider_case_study',
          title: 'Volvo Rider',
          description: 'Volvo Rider reimagined a humble lifestyle accessory as a full-blown automotive launch.',
          noindex: true,
          modules: [
            { src: getImageUrl('/projects/Volvo-Rider/VR-001.jpg'), alt: 'Volvo Rider Hero Image', type: 'single' },
          ],
          creditsTitle: 'Press',
          creditsCopy: `The Drive
Volvo Teases Their Smallest Coupe Ever (https://www.thedrive.com/news/9055/volvo-teases-their-smallest-coupe-ever)
Jalopnik
The New Volvo Rider Is A Freaking Packaging Nightmare (https://www.jalopnik.com/the-new-volvo-rider-is-a-freaking-packaging-nightmare-1794095785/)`
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateVolvoRiderConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

export { VolvoRiderFlexible as VolvoRiderFlexible };

/**
 * Polestar Engineered Case Study Component
 */
const PolestarEngineeredFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generatePolestarEngineeredConfig = async () => {
      try {
        const polestarEngineeredConfig: CaseStudyConfig = {
          storageKey: 'polestar_engineered_case_study',
          title: 'Polestar Engineered',
          description: 'Polestar Engineered represents performance through precision and restraint. The challenge was to translate a highly technical sub-brand into a digital experience that felt as engineered as the cars themselves. Instead of leaning on volume or visual aggression, we focused on clarity, craftsmanship, and control, creating a platform where performance is communicated through detail, discipline, and intent.',
          modules: [
            { src: getImageUrl('/_images/PolestarEngineered/PolestarEngineered-Hero.jpg'), alt: 'Polestar Engineered Hero', type: 'single' },
            { src: getVideoUrl('PolestarEngineered.mp4'), alt: 'Polestar Engineered Video', type: 'video', videoType: 'mp4' },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Polestar Engineered Optimisation'
        };

        setConfig(polestarEngineeredConfig);
      } catch (error) {
        console.error('Error generating Polestar Engineered config:', error);
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'polestar_engineered_case_study',
          title: 'Polestar Engineered',
          description: 'Polestar Engineered represents performance through precision and restraint.',
          modules: [
            { src: getImageUrl('/_images/PolestarEngineered/PolestarEngineered-Hero.jpg'), alt: 'Polestar Engineered Hero', type: 'single' },
          ],
          creditsTitle: 'Info',
          creditsCopy: 'Polestar Engineered Optimisation'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generatePolestarEngineeredConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

export { PolestarEngineeredFlexible as PolestarEngineeredFlexible };

/**
 * Drita Ime App Case Study Component
 */
const DritaImeFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateDritaImeConfig = async () => {
      try {
        const dritaImeConfig: CaseStudyConfig = {
          storageKey: 'drita_ime_case_study',
          title: 'Drita Ime - Albanian language app for kids',
          description: 'Drita Ime started as a personal project. I built the app together with my daughter as a way to teach her my mother tongue and keep our language alive at home. What began as simple alphabet cards quickly became a shared creative process. Recording her voice, testing layouts together, and shaping the experience side by side turned learning into play. The app became a bridge between generations, culture, and technology, designed to make language learning feel warm, intuitive, and joyful.',
          modules: [
            // Hero image
            { src: getImageUrl('/_images/DritaIme/Drita-ime-hero.png'), alt: 'Drita Ime Hero', type: 'single' },
            
            // Promo end image - right after intro text
            { src: getImageUrl('/_images/DritaIme/eb-promo-end.png'), alt: 'Drita Ime Promo End', type: 'single' },
            
            // Two column images - Kid images
            { src: getImageUrl('/_images/DritaIme/Kid-col1.jpg'), alt: 'Drita Ime Kid 1', type: 'twoColumn', pairIndex: 0 },
            { src: getImageUrl('/_images/DritaIme/Kid-col2.jpg'), alt: 'Drita Ime Kid 2', type: 'twoColumn', pairIndex: 0 },
            
            // Text module with Challenge, Opportunity, Solution, Impact
            {
              src: '',
              alt: 'Challenge, Opportunity, Solution, Impact',
              type: 'text',
              text: '**Challenge**\n\nTeaching a minority language at home is hard. Most tools are either outdated, overly academic, or disconnected from how children actually learn and engage today.\n\n**Opportunity**\n\nChildren learn best through interaction, repetition, and emotional connection. By combining playful design, familiar voices, and simple interactions, language learning could become part of everyday family life rather than a forced lesson.\n\n**Solution**\n\nDrita Ime is a calm, focused learning app built for young children and parents. The experience centers on clear visuals, touch-friendly interactions, and authentic voice recordings that make each letter feel alive. Designed and tested together with my children, the app prioritizes warmth, simplicity, and usability over complexity or gamification.\n\n**Impact**\n\nDrita Ime helps families pass their language on in a natural way. It has been adopted by parents looking for a simple, meaningful tool to teach Albanian to their children, turning learning moments into shared experiences that strengthen both language and connection.'
            },
            
            // Two column images - Hands images
            { src: getImageUrl('/_images/DritaIme/Hands-col1.jpg'), alt: 'Drita Ime Hands 1', type: 'twoColumn', pairIndex: 2 },
            { src: getImageUrl('/_images/DritaIme/Hands-col2.jpg'), alt: 'Drita Ime Hands 2', type: 'twoColumn', pairIndex: 2 },
            
            // Two column images - Dritaime images
            { src: getImageUrl('/_images/DritaIme/Dritaime-col1.png'), alt: 'Drita Ime App 1', type: 'twoColumn', pairIndex: 1 },
            { src: getImageUrl('/_images/DritaIme/Dritaime-col2.png'), alt: 'Drita Ime App 2', type: 'twoColumn', pairIndex: 1 },
          ],
          creditsTitle: 'Download App',
          creditsCopy: 'Download Drita Ime on the App Store (https://apps.apple.com/se/app/drita-ime/id6744001210)'
        };

        setConfig(dritaImeConfig);
      } catch (error) {
        console.error('Error generating Drita Ime config:', error);
        // Fallback config
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'drita_ime_case_study',
          title: 'Drita Ime - Albanian language app for kids',
          description: 'Drita Ime started as a personal project. I built the app together with my daughter as a way to teach her my mother tongue and keep our language alive at home.',
          modules: [
            { src: getImageUrl('/_images/DritaIme/Drita-ime-hero.png'), alt: 'Drita Ime Hero', type: 'single' },
          ],
          creditsTitle: 'Download App',
          creditsCopy: 'Download Drita Ime on the App Store (https://apps.apple.com/se/app/drita-ime/id6744001210)'
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateDritaImeConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

export { DritaImeFlexible as DritaImeFlexible };

const WORD_PLAY_CHAPLIN_URL = '/word-play/play/';
const WORD_PLAY_CHANDELIER_URL = '/word-play/chandelier/play/';

const WORD_PLAY_DESCRIPTION = `An experiment in reading with sound.

Word Play began as a side project — a question about whether film could be read the same way it is heard. I built a player that syncs typography to voice: every word lands on screen the instant it is spoken, while the full transcript writes itself in real time beneath it.

Tap any word and the video jumps to that moment. The text becomes the timeline.

The first pieces were speeches and music videos, chosen to stress-test timing, legibility, and scale across different voices and moods. What started as manual transcript alignment became a format — typography locked to the voice, frame for frame. The same engine could run behind podcasts, films, talks, or as a live, reactive VJ system on LED walls.

More Word Play experiments are on the way. I will keep adding new films, speeches, and formats as the project grows.

If you have something in mind — a film, a talk, a podcast, or a live installation — that could use voice-locked typography, get in touch.`;

/**
 * Word Play Case Study Component
 */
const WordPlayFlexible = (): JSX.Element => {
  const [config, setConfig] = useState<CaseStudyConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateWordPlayConfig = async () => {
      try {
        const wordPlayConfig: CaseStudyConfig = {
          storageKey: 'word_play_case_study_v3',
          title: 'Word Play',
          description: WORD_PLAY_DESCRIPTION,
          modules: [
            { src: getImageUrl('/_images/WordPlay/wordplay-thumb.jpg'), alt: 'Word Play', type: 'single' },
          ],
          hideCredits: true,
          creditsTitle: '',
          creditsCopy: '',
          experienceLinks: [
            {
              label: 'Launch Chandelier in WordPlay',
              href: WORD_PLAY_CHANDELIER_URL,
              afterParagraph: 2,
              image: getImageUrl('/_images/WordPlay/sia.jpg'),
              imageAlt: 'Chandelier — Sia Word Play',
            },
            {
              label: 'Launch Chaplin in WordPlay',
              href: WORD_PLAY_CHAPLIN_URL,
              afterParagraph: 2,
              image: getImageUrl('/_images/WordPlay/wordplay.jpg'),
              imageAlt: 'The Great Dictator Word Play',
            },
          ],
          contactSocials: [
            { label: 'Instagram', href: 'https://www.instagram.com/besbujupi' },
            { label: 'LinkedIn', href: 'https://www.linkedin.com/in/besbujupi/' },
            { label: 'X', href: 'https://x.com/besbujupi' },
            { label: 'Behance', href: 'https://www.behance.net/besbujupi' },
          ],
        };

        setConfig(wordPlayConfig);
      } catch (error) {
        console.error('Error generating Word Play config:', error);
        const fallbackConfig: CaseStudyConfig = {
          storageKey: 'word_play_case_study_v3',
          title: 'Word Play',
          description: WORD_PLAY_DESCRIPTION,
          modules: [
            { src: getImageUrl('/_images/WordPlay/wordplay-thumb.jpg'), alt: 'Word Play', type: 'single' },
          ],
          hideCredits: true,
          creditsTitle: '',
          creditsCopy: '',
          experienceLinks: [
            {
              label: 'Launch Chandelier in WordPlay',
              href: WORD_PLAY_CHANDELIER_URL,
              afterParagraph: 2,
              image: getImageUrl('/_images/WordPlay/sia.jpg'),
              imageAlt: 'Chandelier — Sia Word Play',
            },
            {
              label: 'Launch Chaplin in WordPlay',
              href: WORD_PLAY_CHAPLIN_URL,
              afterParagraph: 2,
              image: getImageUrl('/_images/WordPlay/wordplay.jpg'),
              imageAlt: 'The Great Dictator Word Play',
            },
          ],
          contactSocials: [
            { label: 'Instagram', href: 'https://www.instagram.com/besbujupi' },
            { label: 'LinkedIn', href: 'https://www.linkedin.com/in/besbujupi/' },
            { label: 'X', href: 'https://x.com/besbujupi' },
            { label: 'Behance', href: 'https://www.behance.net/besbujupi' },
          ],
        };
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };

    generateWordPlayConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div>Error loading case study</div>;
  }

  return <CaseStudy config={config} />;
};

export { WordPlayFlexible as WordPlayFlexible };

/**
 * Helper function to create a case study with automatic layout detection
 * Usage: createCaseStudy('project_name', 'Project Title', 'Description', images, videos)
 */
export const createCaseStudy = async (
  storageKey: string,
  title: string,
  description: string,
  images: ImageInfo[],
  videos: VideoInfo[] = [],
  videoPosition: number = 1,
  creditsTitle: string = 'Info',
  creditsCopy: string = ''
): Promise<CaseStudyConfig> => {
  return await createFlexibleCaseStudyConfig(
    storageKey,
    title,
    description,
    images,
    videos,
    videoPosition,
    creditsTitle,
    creditsCopy
  );
}; 